import { get } from 'lodash-es';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { baseColumnsDefaultConfigs, defaultColumnConfigurations } from '../../../store/returns/grid/default-column-configuration';
import { emptyArray } from 'ui-components/default-props/empty';
import { GridConfigService } from '../../../services/grid-config-service';
import { ReturnsGridModes } from '../../../enums/returns-grid-mode';

import type { ReturnsGridMode } from '../../../enums/returns-grid-mode';
import type { ReturnsGridColumnConfiguration, ReturnsGridReturn, ReturnsGridState } from './returns.grid.types';
import type { RootState } from 'store/root-state';

type ReturnsGridViewProps = {
  statusViewId: string;
  mode?: ReturnsGridMode;
};

export const selectReturns = (state: ReturnsGridState): ReturnsGridReturn[] => get(state, 'returns', emptyArray as ReturnsGridReturn[]);
export const selectPage = (state: ReturnsGridState): number => get(state, 'page');
export const selectPageSize = (state: ReturnsGridState): number => get(state, 'pageSize');
export const selectTotalRecords = (state: ReturnsGridState): number => get(state, 'totalRecords');
export const selectTotalPages = (state: ReturnsGridState): number => get(state, 'totalPages');
export const selectViewStartingCount = (state: ReturnsGridState): number => get(state, 'viewStartingCount');
export const selectViewEndingCount = (state: ReturnsGridState): number => get(state, 'viewEndingCount');
export const selectIsLoading = (state: ReturnsGridState): boolean => get(state, 'isLoading');

const noReceivedDateColumnSelector: (baseCols: ReturnsGridColumnConfiguration[]) => ReturnsGridColumnConfiguration[] = createSelector(
  (baseCols: ReturnsGridColumnConfiguration[]) => baseCols,
  (baseCols): ReturnsGridColumnConfiguration[] => {
    const noReceivedDate = baseCols.filter((col) => col.id !== 'received-date');

    if (noReceivedDate.length === 0) {
      return emptyArray as ReturnsGridColumnConfiguration[];
    }

    return noReceivedDate;
  },
);

// Second input is used only as a cache key discriminator (one entry per statusViewId + mode)
const mergedConfigurationSelector: (
  viewConfiguration: ReturnsGridColumnConfiguration[],
  cacheKey: string,
) => ReturnsGridColumnConfiguration[] = createCachedSelector(
  (viewConfiguration: ReturnsGridColumnConfiguration[]) => viewConfiguration,
  (_: ReturnsGridColumnConfiguration[], cacheKey: string) => cacheKey,
  (viewConfiguration): ReturnsGridColumnConfiguration[] =>
    GridConfigService.mergeUserAndDefaultConfigs(viewConfiguration, defaultColumnConfigurations),
)((_: ReturnsGridColumnConfiguration[], cacheKey: string) => cacheKey);

export const selectUserGridConfigOrDefault = (
  state: ReturnsGridState,
  { statusViewId, mode = ReturnsGridModes.Shipment }: ReturnsGridViewProps,
): ReturnsGridColumnConfiguration[] => {
  const savedConfig: ReturnsGridColumnConfiguration[] | undefined = get(state, `columnConfiguration.${statusViewId}.${mode}`);

  if (savedConfig) {
    return mergedConfigurationSelector(savedConfig, `${statusViewId}_${mode}`);
  }

  return noReceivedDateColumnSelector(baseColumnsDefaultConfigs);
};

export const selectCurrentOrderByColumn = (
  state: ReturnsGridState,
  { statusViewId, mode = ReturnsGridModes.Shipment }: ReturnsGridViewProps,
): ReturnsGridColumnConfiguration | undefined => {
  const gridConfig = selectUserGridConfigOrDefault(state, { statusViewId, mode }) || emptyArray;
  return gridConfig.find((col) => !!col.sorted);
};

export const selectReturnsGridCurrentOrderByColumn = (
  state: RootState,
  props: ReturnsGridViewProps,
): ReturnsGridColumnConfiguration | undefined => selectCurrentOrderByColumn(get(state, 'returns.grid'), props);
export const selectReturnsGridIsLoading = (state: RootState): boolean => selectIsLoading(get(state, 'returns.grid'));
export const selectReturnsGridPage = (state: RootState): number => selectPage(get(state, 'returns.grid'));
export const selectReturnsGridPageSize = (state: RootState): number => selectPageSize(get(state, 'returns.grid'));
export const selectReturnsGridReturns = (state: RootState): ReturnsGridReturn[] => selectReturns(get(state, 'returns.grid'));
export const selectReturnsGridTotalPages = (state: RootState): number => selectTotalPages(get(state, 'returns.grid'));
export const selectReturnsGridTotalRecords = (state: RootState): number => selectTotalRecords(get(state, 'returns.grid'));
export const selectReturnsGridUserGridConfigOrDefault = (state: RootState, props: ReturnsGridViewProps): ReturnsGridColumnConfiguration[] =>
  selectUserGridConfigOrDefault(get(state, 'returns.grid'), props);
export const selectReturnsGridViewEndingCount = (state: RootState): number => selectViewEndingCount(get(state, 'returns.grid'));
export const selectReturnsGridViewStartingCount = (state: RootState): number => selectViewStartingCount(get(state, 'returns.grid'));
